import { useState } from 'react';
import { Dialog, DialogPanel } from '@headlessui/react';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import axios from 'axios';
import { appConfig } from 'src/config/appConfig';
import Button from 'src/components/button/Button';
import { useNotification } from 'src/hooks/useNotification';

interface Props {
  raffleId: string;
  ticketsSold: number;
  status: string;
  userId: string;
  onCancelled: () => void;
}

const CancelRaffleBtn = ({ raffleId, ticketsSold, status, userId, onCancelled }: Props) => {
  const [showConfirm, setShowConfirm] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const notification = useNotification();
  const tooltipId = `cancel-raffle-tooltip-${raffleId}`;

  const canCancel = ticketsSold === 0 && (status === 'DRAFT' || status === 'LIVE');

  const handleCancel = async () => {
    setIsCancelling(true);
    try {
      await axios.patch(`${appConfig.serviceUrl}/raffles/${raffleId}/cancel`, { userId });
      setShowConfirm(false);
      notification('Raffle has been cancelled successfully', { type: 'success' });
      onCancelled();
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to cancel raffle';
      notification(msg, { type: 'error' });
    } finally {
      setIsCancelling(false);
    }
  };

  const btnClass = 'h-[42px] w-full bg-brand-granite rounded-lg text-lg font-semibold text-brand-magnolia';

  if (!canCancel) {
    return (
      <>
        <button
          className={`${btnClass} opacity-50 cursor-not-allowed`}
          data-tooltip-id={tooltipId}
        >
          Cancel Raffle
        </button>

        <Tooltip
          id={tooltipId}
          place="top"
          offset={13}
          className="!bg-grayscale-900 !text-grayscale-200 !rounded-lg !py-1.5 !px-3 !text-sm !w-[245px] !text-center"
        >
          Cancellation is no longer possible as at least one ticket has been sold.
        </Tooltip>
      </>
    );
  }

  return (
    <>
      <button className={btnClass} onClick={() => setShowConfirm(true)}>
        Cancel Raffle
      </button>

      <Dialog
        open={showConfirm}
        as="div"
        className="relative z-10 focus:outline-none"
        onClose={() => setShowConfirm(false)}
      >
        <div className="overflow-y-auto fixed inset-0 w-screen bg-[#1B1B1F99] backdrop-blur-sm">
          <div className="flex justify-center items-center min-h-full p-4">
            <DialogPanel className="w-full max-w-md rounded-2xl bg-grayscale-800 p-6 space-y-4">
              <h3 className="text-xl font-semibold text-brand-magnolia">Cancel Raffle</h3>

              <p className="text-grayscale-300">
                Are you sure you want to cancel this raffle? This action cannot be undone.
              </p>

              <div className="flex gap-3 justify-end">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowConfirm(false)}
                  disabled={isCancelling}
                >
                  Keep Raffle
                </Button>

                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleCancel}
                  disabled={isCancelling}
                >
                  {isCancelling ? 'Cancelling...' : 'Yes, Cancel'}
                </Button>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default CancelRaffleBtn;
